import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Search, Globe, Lock, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["all", "photo", "video", "audio", "document", "logo", "brand"];

export default function Media() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [cat, setCat] = useState("all");
  const [folder, setFolder] = useState("all");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMeta, setUploadMeta] = useState({ folder: "", caption: "", alt_text: "", credit: "", is_public: false });
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("neos_token") : "";

  const load = useCallback(async () => {
    const params = { category: cat };
    if (q) params.q = q;
    if (folder !== "all") params.folder = folder;
    const [{ data: rows }, { data: fs }] = await Promise.all([
      api.get("/media", { params }),
      api.get("/media/folders"),
    ]);
    setItems(rows);
    setFolders(fs);
  }, [cat, folder, q]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    const category = file.type.startsWith("image/") ? "photo" : file.type.startsWith("video/") ? "video" : file.type.startsWith("audio/") ? "audio" : "document";
    fd.append("category", category);
    fd.append("folder", uploadMeta.folder || "");
    fd.append("caption", uploadMeta.caption || "");
    fd.append("alt_text", uploadMeta.alt_text || "");
    fd.append("credit", uploadMeta.credit || "");
    fd.append("is_public", uploadMeta.is_public ? "true" : "false");
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setUploadMeta({ folder: "", caption: "", alt_text: "", credit: "", is_public: false });
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    await api.delete(`/media/${id}`);
    toast.success("Removed"); load();
  };

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Media Library</div>
          <h1 className="font-serif text-4xl mt-1">Assets</h1>
          <p className="text-sm text-ink-soft mt-1">Every image, video, and document the institution stores. Mark an asset public to use it on the website.</p>
        </div>
        <label className="pill pill-primary text-sm cursor-pointer" data-testid="media-upload-label">
          <Upload className="h-4 w-4" />
          {uploading ? "Uploading…" : "Upload file"}
          <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="media-upload-input" />
        </label>
      </div>

      {/* Pre-upload metadata */}
      <details className="bg-admin-surface border border-line rounded-xl" data-testid="upload-metadata">
        <summary className="px-4 py-2 cursor-pointer text-xs uppercase tracking-widest text-ink-soft select-none">
          Set folder / caption / alt / credit before uploading (optional)
        </summary>
        <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 border-t border-line text-sm">
          <Field label="Folder"><Input placeholder="e.g. dubbi-renders" value={uploadMeta.folder} onChange={e=>setUploadMeta(p=>({...p, folder: e.target.value}))} data-testid="upload-folder" /></Field>
          <Field label="Caption"><Input value={uploadMeta.caption} onChange={e=>setUploadMeta(p=>({...p, caption: e.target.value}))} data-testid="upload-caption" /></Field>
          <Field label="Alt text"><Input value={uploadMeta.alt_text} onChange={e=>setUploadMeta(p=>({...p, alt_text: e.target.value}))} data-testid="upload-alt" /></Field>
          <Field label="Credit"><Input placeholder="Photographer / source" value={uploadMeta.credit} onChange={e=>setUploadMeta(p=>({...p, credit: e.target.value}))} data-testid="upload-credit" /></Field>
          <label className="flex items-center gap-2 pt-6 cursor-pointer">
            <input type="checkbox" checked={uploadMeta.is_public} onChange={e=>setUploadMeta(p=>({...p, is_public: e.target.checked}))} className="w-4 h-4 accent-forest" data-testid="upload-public" />
            <span className="text-xs text-ink">Public (usable on website)</span>
          </label>
        </div>
      </details>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <Input placeholder="Search filename, caption, alt, or tag" data-testid="media-search" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-48" data-testid="media-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="folder-chips">
        {["all", ...folders, "unfiled"].map(f => (
          <button key={f} onClick={() => setFolder(f)} data-testid={`folder-chip-${f}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${folder === f ? "bg-forest text-bone border-forest" : "bg-bone border-line text-ink hover:border-forest"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map(m => (
          <div key={m.id} className="group bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid={`media-item-${m.id}`}>
            <div className="aspect-square bg-bone/50 flex items-center justify-center overflow-hidden">
              {m.category === "photo" ? (
                <img src={`${API}/media/file/${m.id}?auth=${encodeURIComponent(token || "")}`} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" />
              ) : (
                <div className="text-ink-soft font-serif text-2xl">{m.category}</div>
              )}
            </div>
            <div className="p-3 space-y-1.5">
              <div className="text-xs text-ink truncate font-medium">{m.caption || m.original_filename}</div>
              {m.folder && <div className="text-[10px] text-ink-soft truncate">📁 {m.folder}</div>}
              {m.credit && <div className="text-[10px] text-ink-soft/70 truncate italic">© {m.credit}</div>}
              <div className="flex items-center justify-between mt-2 gap-2">
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">{m.category}</div>
                  {m.is_public
                    ? <Badge className="bg-forest text-bone text-[9px]"><Globe className="h-2 w-2 mr-1" strokeWidth={1.5} />Public</Badge>
                    : <Badge className="bg-bone/60 text-ink-soft border-line text-[9px]"><Lock className="h-2 w-2 mr-1" strokeWidth={1.5} />Private</Badge>}
                </div>
                <button onClick={() => setEditing(m)} data-testid={`media-edit-${m.id}`} className="text-ink-soft hover:text-forest"><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
                <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
              </div>
            </div>
          </div>
        ))}
        {!items.length && <div className="col-span-full text-ink-soft text-center py-16">No media yet. Upload the first asset.</div>}
      </div>

      {editing && (
        <EditMediaDialog media={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EditMediaDialog({ media, onClose, onSaved }) {
  const [f, setF] = useState({
    caption: media.caption || "",
    alt_text: media.alt_text || "",
    credit: media.credit || "",
    folder: media.folder || "",
    is_public: !!media.is_public,
  });
  const save = async () => {
    try {
      await api.patch(`/media/${media.id}`, f);
      toast.success("Metadata updated");
      onSaved();
    } catch { toast.error("Update failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-admin-surface border-line" data-testid="media-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit media metadata</DialogTitle>
          <p className="text-xs text-ink-soft truncate">{media.original_filename}</p>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Caption"><Input value={f.caption} onChange={e=>setF({...f, caption: e.target.value})} data-testid="edit-caption" /></Field>
          <Field label="Alt text"><Textarea rows={2} value={f.alt_text} onChange={e=>setF({...f, alt_text: e.target.value})} data-testid="edit-alt" /></Field>
          <Field label="Credit"><Input value={f.credit} onChange={e=>setF({...f, credit: e.target.value})} data-testid="edit-credit" /></Field>
          <Field label="Folder"><Input placeholder="e.g. drone / renders / land" value={f.folder} onChange={e=>setF({...f, folder: e.target.value})} data-testid="edit-folder" /></Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.is_public} onChange={e=>setF({...f, is_public: e.target.checked})} className="w-4 h-4 accent-forest" data-testid="edit-public" />
            <span className="text-sm text-ink">Public — usable on the website (unauthenticated visitors can view)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-line">
          <Button variant="outline" className="border-line" onClick={onClose}><X className="h-4 w-4 mr-1" strokeWidth={1.5} />Cancel</Button>
          <Button onClick={save} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid="edit-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="overline mb-1 block">{label}</Label>{children}</div>;
}
